<template>
  <DsRadioGroup v-model="proxy" :disabled="disabled" :variant="variant">
    <div
      :class="{
        'my-2 flex flex-row space-x-3': inline,
        'space-y-3': !inline,
        'text-sm text-gray-700': inputFontSize,
      }"
    >
      <DsRadioButton
        v-for="option in optionsProxy"
        :key="option.value"
        :label="option.label"
        :value="option.value.toString()"
        :disabled="option.disabled ?? false"
      />
    </div>
  </DsRadioGroup>
</template>

<script lang="ts" setup>
import {
  DsRadioButton,
  DsRadioGroup,
  RadioGroupVariant,
} from '@demvsystems/design-components';
import { computed, type ComputedRef } from 'vue';

import type { RadioGroupOption } from '@/components/form-elements/RadioGroup/types';
import { useEventBus } from '@/utils/useEventBus/useEventBus';

interface Props {
  modelValue: string | boolean | number;
  options: RadioGroupOption[] | ComputedRef<RadioGroupOption[]>;
  inline?: boolean;
  asBoolean?: boolean;
  inputFontSize?: boolean;
  disabled?: boolean;
  path?: string;
  asNumber?: boolean;
  variant?: RadioGroupVariant;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  path: undefined,
  asNumber: false,
  variant: 'default',
});

const emit = defineEmits(['update:modelValue']);

const proxy = computed({
  get: () => props.modelValue.toString(),
  set: (val) => {
    let finalValue: string | boolean | number = val;
    if (props.asBoolean) {
      finalValue = val === 'true';
    }

    if (props.asNumber) {
      finalValue = Number(val);
    }

    emit('update:modelValue', finalValue);
    if (props.path !== undefined) {
      useEventBus().emit('radioValueChangedManually', props.path);
    }
  },
});

const optionsProxy = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((o) => ({
      ...o,
      value: o.value.toString(),
    }));
  }

  return props.options.value.map((o) => ({
    ...o,
    value: o.value.toString(),
  }));
});
</script>
